import React, { useEffect, useState, useRef } from "react";
import axiosInstance from "../axiosinstance";
import { IoIosArrowRoundBack, IoIosSearch } from "react-icons/io";
import { CiCircleInfo } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { IoCloseOutline } from "react-icons/io5";
import { PiSortAscendingThin } from "react-icons/pi";
import "../Tables/Table.css";

const ArchivedClients = ({ isSidebarExpanded }) => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [isChecked, setChecked] = useState(false);

  const fetchClients = async (status = "Archive") => {
    try {
      const response = await axiosInstance.get(
        `/api/get_all_clients?page_status=${status}`
      );
      setClients(response.data.data);
    } catch (error) {
      console.error("Error fetching clients data:", error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (!query.trim()) {
      fetchClients();
      return;
    }

    try {
      const response = await axiosInstance.get(`/api/search_clients`, {
        params: {
          query,
          page_status: "Archive",
        },
      });

      setClients(response.data.data || []);
    } catch (error) {
      console.error("Error fetching search results:", error.message);
    }
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";

    const sortedClients = [...clients].sort((a, b) => {
      const aValue =
        key === "contact_details" && a.contact_details?.length
          ? a.contact_details[0].contact_name.toLowerCase()
          : a[key];

      const bValue =
        key === "contact_details" && b.contact_details?.length
          ? b.contact_details[0].contact_name.toLowerCase()
          : b[key];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setClients(sortedClients);
    setSortConfig({ key, direction });
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const handleCheckboxChange = (firmId) => {
    setSelectedFirms((prevSelected) =>
      prevSelected.includes(firmId)
        ? prevSelected.filter((id) => id !== firmId)
        : [...prevSelected, firmId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFirms([]);
    } else {
      setSelectedFirms(clients.map((client) => client.client_id));
    }
    setSelectAll(!selectAll);
  };

  const handleRestoreUser = async () => {
    try {
      const response = await axiosInstance.put(`/api/unarchive_clients`, {
        ids: selectedFirms,
      });

      if (response.status === 200) {
        fetchClients();
        navigate("/client-management");
        setSelectAll(false);
        setChecked(false);
      } else {
        console.error("Failed to restore departments");
      }
    } catch (error) {
      console.error("An error occurred while restoring departments:", error);
    }
  };

  return (
    <div
      className={`table-container archived-clients-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="archived-clients-content">
        <div className="table-header">
          {selectedFirms.length > 0 ? (
            <div className="table-header-content archive-header">
              <button className="delete" onClick={handleRestoreUser}>
                Unarchive
              </button>

              <button
                className="action-header-close-button"
                onClick={() => setSelectedFirms([])}
              >
                <IoCloseOutline className="action-header-close-icon" />
              </button>
            </div>
          ) : (
            <div className="table-header-content">
              <div className="table-left-side">
                <div onClick={() => navigate(-1)} className="back-button">
                  <IoIosArrowRoundBack />
                </div>
                <h2 className="jost-bold">Archived Clients</h2>
              </div>
              <div className="table-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search ..."
                    className="search-bar"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <IoIosSearch className="search-bar-icon" />
                </div>

                <button
                  className="archived-clients-sort-button"
                  onClick={handleSortpopup}
                >
                  <span className="action-icon">
                    <PiSortAscendingThin />
                  </span>
                </button>

                {isSortPopup && (
                  <div className="sort-popup-container" ref={containerRef}>
                    <div className="sort-popup-header">
                      <span className="sort-title">Sort By</span>
                    </div>
                    <div className="sort-popup-content">
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("clients_company_name")}
                      >
                        Client Company Name
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("contact_details")}
                      >
                        Contact Person Name
                      </button>
                      <button
                        className="sort-button sort-button-options"
                        onClick={() => handleSort("subsidiary_company")}
                      >
                        Billing Company
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="data-list-body archived-clients-data-body">
          <div className="data-list-scroll-body archived-clients-scroll-body">
            <table className="table multilevel-data archived-clients-table">
              <thead className="sticky-header">
                <tr className="table-row">
                  <th>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Sr. No</th>
                  <th>Client Company Name</th>
                  <th>Contact Person Name</th>
                  <th>Billing Company</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data-text">
                      Currently, there are no records to display
                    </td>
                  </tr>
                ) : (
                  clients.map((client, index) => {
                    const contactPersonName =
                      client.contact_details?.[0]?.contact_name || "NA";
                    const contactEmail =
                      client.contact_details?.[0]?.contact_email ||
                      "Not Available";
                    const contactAddress = `${client.office_number || "NA"}, ${
                      client.landmark || "NA"
                    }, ${client.area || "NA"}, ${client.pin || "NA"}`;

                    return (
                      <tr key={client.client_id} className="hoverable-row">
                        <td className="center-align">
                          <input
                            type="checkbox"
                            checked={selectedFirms.includes(client.client_id)}
                            onChange={() =>
                              handleCheckboxChange(client.client_id)
                            }
                          />
                        </td>
                        <td className="center-align">{index + 1}</td>
                        <td>{client.clients_company_name || "NA"}</td>
                        <td>
                          <Tooltip
                            title={
                              <>
                                <div>
                                  <strong>Email:</strong> {contactEmail}
                                </div>
                                <div>
                                  <strong>Address:</strong> {contactAddress}
                                </div>
                              </>
                            }
                            arrow
                          >
                            <span className="archived-clients-contact-cell">
                              {contactPersonName}
                              <span className="hover-icon">
                                <CiCircleInfo />
                              </span>
                            </span>
                          </Tooltip>
                        </td>
                        <td>{client.subsidiary_company || "NA"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivedClients;
